import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import lottie from 'lottie-web';
import type { AnimationItem } from 'lottie-web';
import ButtonLoader from '../ButtonLoader/ButtonLoader';

interface LottieAnimationProps {
  animationData: string | object; // URL | JSON-строка | объект
  style?: CSSProperties;
  loop?: boolean;
  autoplay?: boolean;
  renderer?: 'svg' | 'canvas';
}

export const LottieAnimation = ({
  animationData,
  style,
  loop = true,
  autoplay = true,
  renderer = 'canvas',
}: LottieAnimationProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const [data, setData] = useState<object | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const anim: AnimationItem | null = null;

    async function loadData() {
      try {
        let json: object;

        if (typeof animationData === 'string') {
          if (
            animationData.startsWith('http') ||
            animationData.startsWith('/')
          ) {
            const res = await fetch(animationData);
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            json = await res.json();
          } else {
            json = JSON.parse(animationData);
          }
        } else {
          json = animationData;
        }

        if (cancelled) return;
        setData(json);
      } catch (e: any) {
        if (!cancelled) setError(e.message ?? 'Failed to load animation');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadData();

    return () => {
      cancelled = true;
      anim?.destroy();
    };
  }, [animationData]);

  // создаём и уничтожаем анимацию
  useEffect(() => {
    if (!ref.current || !data) return;
    const anim = lottie.loadAnimation({
      container: ref.current,
      renderer,
      loop,
      autoplay,
      animationData: data,
      rendererSettings: {
        preserveAspectRatio: 'xMidYMid meet',
        progressiveLoad: true,
      },
    });

    // ускорение: отключить субфреймы
    anim.setSubframe(false);

    return () => {
      anim.destroy();
    };
  }, [data, renderer, loop, autoplay]);

  const containerStyle: CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    overflow: 'hidden',
    marginLeft: '5px',
    ...(style || {}),
  };

  if (error) return <div style={containerStyle}>Ошибка: {error}</div>;
  if (loading)
    return (
      <div style={containerStyle}>
        <ButtonLoader />
      </div>
    );

  return <div ref={ref} style={containerStyle} />;
};
